import StartupPageTitle from '../components/StartupPageTitle';
import { UsersThree as PageHeadingIcon } from '@phosphor-icons/react';
import React, { useEffect, useState } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { formatDate } from '@/utils/datetime';
import { Plus, Crown, Shield, User, Trash, Copy, X, Check, Warning, Sparkle } from '@phosphor-icons/react';

const ROLE_BADGES = {
  owner: { Icon: Crown, color: 'text-warning', bg: 'bg-warning/10', label: 'Owner' },
  admin: { Icon: Shield, color: 'text-primary', bg: 'bg-primary/10', label: 'Admin' },
  member: { Icon: User, color: 'text-text-secondary', bg: 'bg-text-muted/10', label: 'Member' },
};

const Team = ({ user, hideHeader = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);

  const load = async () => {
    try {
      const res = await api.get('/team/members');
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load team');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const myRole = data?.your_role;
  const canInvite = myRole === 'owner' || myRole === 'admin';
  const isStartup = data?.kind === 'startup';
  const accent = isStartup ? 'bg-success' : 'bg-primary';
  const accentText = isStartup ? 'text-success' : 'text-primary';

  if (loading) return <div className="p-8 text-text-muted">Loading team...</div>;
  if (!data) return <div className="p-8 text-text-muted">No team data.</div>;

  const seatsUsed = data.seats_used || 0;
  const tier = data.current_tier;
  const nextTier = data.next_seat_tier;
  const overTier = nextTier && nextTier.label !== tier?.label;

  return (
    <div className="p-6 lg:p-8 max-w-5xl">
      <div className="flex items-start justify-between mb-8 startup-page-header">
        {!hideHeader && <div>
          <StartupPageTitle icon={PageHeadingIcon} className="text-3xl font-heading font-bold text-text-primary">Team</StartupPageTitle>
          <p className="text-text-secondary text-sm mt-1">Manage who has access to <strong>{data.fund_name}</strong>.</p>
        </div>}
        {canInvite && (
          <button onClick={() => setShowInviteModal(true)} data-testid="invite-member-btn"
            className={`px-4 py-2.5 ${accent} text-white rounded-lg text-sm font-semibold hover:opacity-90 flex items-center gap-2`}>
            <Plus size={16} weight="bold" /> Invite member
          </button>
        )}
      </div>

      {/* Seat / Pricing card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="border border-border rounded-lg p-5 bg-surface">
          <div className="text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-2">Seats used</div>
          <div className="text-4xl font-heading font-black text-text-primary tabular-nums">{seatsUsed}</div>
          {data.pending_count > 0 && <div className="text-xs text-text-secondary mt-1">+ {data.pending_count} pending invitation{data.pending_count > 1 ? 's' : ''}</div>}
        </div>
        <div className="border border-border rounded-lg p-5 bg-surface">
          <div className="text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-2">Current tier</div>
          <div className={`text-2xl font-heading font-black ${accentText}`}>{tier?.label || '—'}</div>
          <div className="text-xs text-text-secondary mt-1">₹{tier?.price_per_seat?.toLocaleString('en-IN') || '—'} / seat / month</div>
        </div>
        <div className="border border-border rounded-lg p-5 bg-surface">
          <div className="text-[10px] uppercase tracking-wider text-text-muted font-semibold mb-2">Monthly subtotal</div>
          <div className="text-2xl font-heading font-black text-text-primary tabular-nums">
            ₹{((tier?.price_per_seat || 0) * seatsUsed).toLocaleString('en-IN')}
          </div>
          <div className="text-xs text-text-secondary mt-1">+ 18% GST</div>
        </div>
      </div>

      {/* Soft tier-change banner */}
      {overTier && (
        <div className="mb-8 p-4 border border-info/30 bg-info/5 rounded-lg flex items-start gap-3" data-testid="tier-upgrade-banner">
          <Sparkle size={18} weight="fill" className="text-info flex-shrink-0 mt-0.5" />
          <div className="text-sm text-text-primary">
            Adding one more seat moves you to <strong>{nextTier.label}</strong> tier — <strong>₹{nextTier.price_per_seat.toLocaleString('en-IN')} per seat</strong>. Volume discount kicks in automatically.
          </div>
        </div>
      )}

      {/* Members list */}
      <div className="border border-border rounded-lg bg-surface overflow-hidden mb-8">
        <div className="px-5 py-3 border-b border-border bg-background/40">
          <div className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">Members</div>
        </div>
        <div className="divide-y divide-border">
          {data.members.map(m => {
            const badge = ROLE_BADGES[m.role] || ROLE_BADGES.member;
            const BIcon = badge.Icon;
            const isMe = m.user_id === user.id;
            const canRemove = (myRole === 'owner' && m.role !== 'owner') || (myRole === 'admin' && m.role === 'member') || isMe && m.role !== 'owner';
            return (
              <div key={m.user_id} className="px-5 py-4 flex items-center gap-4" data-testid={`member-row-${m.user_id}`}>
                <div className={`w-10 h-10 rounded-full ${badge.bg} flex items-center justify-center flex-shrink-0`}>
                  <BIcon size={18} weight="fill" className={badge.color} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-text-primary truncate">
                    {m.full_name || m.email} {isMe && <span className="text-text-muted font-normal text-xs">(you)</span>}
                  </div>
                  <div className="text-xs text-text-secondary truncate">{m.email}</div>
                </div>
                <div className={`text-[10px] uppercase font-bold tracking-wider px-2.5 py-1 rounded ${badge.bg} ${badge.color}`}>{badge.label}</div>
                {myRole === 'owner' && m.role !== 'owner' && (
                  <RoleSelect memberId={m.user_id} currentRole={m.role} onUpdated={load} />
                )}
                {canRemove && !isMe && (
                  <button onClick={async () => {
                    if (!window.confirm(`Remove ${m.full_name || m.email}?`)) return;
                    try {
                      await api.delete(`/team/members/${m.user_id}`);
                      toast.success('Member removed');
                      load();
                    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                  }} className="p-2 text-text-muted hover:text-danger" data-testid={`remove-member-${m.user_id}`}>
                    <Trash size={16} weight="bold" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Pending invitations */}
      {data.pending_invitations && data.pending_invitations.length > 0 && (
        <div className="border border-border rounded-lg bg-surface overflow-hidden">
          <div className="px-5 py-3 border-b border-border bg-background/40">
            <div className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">Pending invitations · {data.pending_invitations.length}</div>
          </div>
          <div className="divide-y divide-border">
            {data.pending_invitations.map(inv => (
              <div key={inv.id} className="px-5 py-4 flex items-center gap-4" data-testid={`pending-row-${inv.id}`}>
                <div className="w-10 h-10 rounded-full bg-warning/10 flex items-center justify-center flex-shrink-0">
                  <Warning size={18} weight="fill" className="text-warning" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-text-primary truncate">{inv.invited_email}</div>
                  <div className="text-xs text-text-secondary">Invited by {inv.invited_by_name} · {formatDate(inv.created_at)}</div>
                </div>
                <span className="text-[10px] uppercase font-bold tracking-wider px-2.5 py-1 rounded bg-warning/10 text-warning">{inv.role}</span>
                <button onClick={() => {
                  const url = `${window.location.origin}/accept-team-invite/${inv.token}`;
                  navigator.clipboard.writeText(url);
                  toast.success('Invite link copied');
                }} className="p-2 text-text-muted hover:text-text-primary" title="Copy invite link" data-testid={`copy-invite-${inv.id}`}>
                  <Copy size={16} weight="bold" />
                </button>
                {canInvite && (
                  <button onClick={async () => {
                    try {
                      await api.delete(`/team/invitations/${inv.id}`);
                      toast.success('Invitation revoked');
                      load();
                    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                  }} className="p-2 text-text-muted hover:text-danger" data-testid={`revoke-invite-${inv.id}`}>
                    <Trash size={16} weight="bold" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {showInviteModal && <InviteModal onClose={() => setShowInviteModal(false)} onInvited={load} myRole={myRole} accent={accent} />}
    </div>
  );
};

const RoleSelect = ({ memberId, currentRole, onUpdated }) => {
  const update = async (newRole) => {
    if (newRole === currentRole) return;
    try {
      await api.patch(`/team/members/${memberId}`, { role: newRole });
      toast.success(`Role updated to ${newRole}`);
      onUpdated();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  return (
    <select value={currentRole} onChange={e => update(e.target.value)}
      className="text-xs bg-background border border-border rounded px-2 py-1 text-text-primary focus:outline-none focus:border-primary"
      data-testid={`role-select-${memberId}`}>
      <option value="member">Member</option>
      <option value="admin">Admin</option>
    </select>
  );
};

const InviteModal = ({ onClose, onInvited, myRole, accent }) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [submitting, setSubmitting] = useState(false);
  const [inviteUrl, setInviteUrl] = useState('');

  const submit = async () => {
    if (!email.includes('@')) { toast.error('Valid email required'); return; }
    setSubmitting(true);
    try {
      const res = await api.post('/team/invite', { email, role });
      setInviteUrl(res.data.invite_url);
      onInvited();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-surface rounded-xl border border-border w-full max-w-md p-6" data-testid="invite-modal">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-heading font-bold text-text-primary">Invite team member</h2>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary"><X size={18} /></button>
        </div>
        {inviteUrl ? (
          <div data-testid="invite-success">
            <div className="flex items-center gap-2 text-success mb-3"><Check size={16} weight="bold" /> Invitation created</div>
            <p className="text-xs text-text-secondary mb-3">Share this link with your teammate. They'll need to log in or sign up with the same email.</p>
            <div className="flex gap-2">
              <input value={inviteUrl} readOnly className="flex-1 bg-background border border-border rounded-lg px-3 py-2 text-xs text-text-primary font-mono" />
              <button onClick={() => { navigator.clipboard.writeText(inviteUrl); toast.success('Copied'); }}
                className="px-3 bg-background border border-border rounded-lg text-text-primary hover:border-primary"><Copy size={16} weight="bold" /></button>
            </div>
            <button onClick={onClose} className="w-full mt-5 px-4 py-2.5 bg-background border border-border rounded-lg text-sm font-medium text-text-primary hover:border-primary">Done</button>
          </div>
        ) : (
          <>
            <label className="block text-xs uppercase tracking-wider text-text-secondary mb-2">Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="teammate@example.com"
              className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm text-text-primary focus:outline-none focus:border-primary mb-4" data-testid="invite-email-input" />
            <label className="block text-xs uppercase tracking-wider text-text-secondary mb-2">Role</label>
            <div className="grid grid-cols-2 gap-2 mb-6">
              <button type="button" onClick={() => setRole('member')}
                className={`py-2.5 rounded-lg border text-sm font-medium ${role === 'member' ? 'bg-primary text-white border-primary' : 'bg-background border-border text-text-secondary'}`} data-testid="invite-role-member">
                Member
              </button>
              <button type="button" onClick={() => setRole('admin')} disabled={myRole !== 'owner'}
                className={`py-2.5 rounded-lg border text-sm font-medium ${role === 'admin' ? 'bg-primary text-white border-primary' : 'bg-background border-border text-text-secondary'} disabled:opacity-40`} data-testid="invite-role-admin">
                Admin {myRole !== 'owner' && '(owner only)'}
              </button>
            </div>
            <button onClick={submit} disabled={submitting} className={`w-full py-3 ${accent} text-white rounded-lg font-semibold disabled:opacity-50`} data-testid="invite-submit-btn">
              {submitting ? 'Creating...' : 'Create invitation'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Team;
